import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useCallback } from 'react';
// material
import { Box, TextField, IconButton, Typography, Popover } from '@mui/material';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
// hooks
import useAuth from '../../hooks/useAuth';
// redux
import { useDispatch } from '../../redux/store';
import { addMessage } from '../../redux/slices/internalChat';
// utils
import axios from '../../utils/axios';
// components
import Iconify from '../../components/Iconify';
import RecorderPlayer from '../../components/RecorderPlayer';

// ----------------------------------------------------------------------

InternalChatInput.propTypes = {
    chatId: PropTypes.string.isRequired,
    chatMode: PropTypes.string,
    chatOwner: PropTypes.any,
    participants: PropTypes.array,
    replyingTo: PropTypes.object,
    onCancelReply: PropTypes.func
};

export default function InternalChatInput({ chatId, chatMode, chatOwner, participants, replyingTo, onCancelReply }) {
    const dispatch = useDispatch();
    const { user } = useAuth();
    const isMountedRef = useRef(null);
    const fileInputRef = useRef(null);

  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);

  const openEmoji = Boolean(emojiAnchorEl);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleOpenEmoji = (event) => {
    setEmojiAnchorEl(event.currentTarget);
  };

  const handleCloseEmoji = () => {
    setEmojiAnchorEl(null);
  };

  const handleEmojiSelect = (emoji) => {
    setMessage((prev) => prev + emoji.native);
  };

  const handleAttach = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const { files } = event.target;
    if (files && files.length > 0) {
      const selectedFile = files[0];
      selectedFile.preview = URL.createObjectURL(selectedFile);
      setFile(selectedFile);
    }
  };

  const handlePaste = (e) => {
    if (e.clipboardData && e.clipboardData.files.length > 0) {
      const pastedFile = e.clipboardData.files[0];
      if (pastedFile.type.startsWith('image/') || pastedFile.type.startsWith('video/')) {
        e.preventDefault();
        pastedFile.preview = URL.createObjectURL(pastedFile);
        setFile(pastedFile);
      }
    }
  };

  const handleAudioSend = useCallback(
    async (audioFile) => {
      try {
        setIsRecording(false);
        const formData = new FormData();
        formData.append('attachment', audioFile);
        const response = await axios.post(`/api/internal-chat/${chatId}/messages`, formData);
        if (response.data && isMountedRef.current) {
          dispatch(addMessage({ conversationId: chatId, message: response.data }));
        }
        if (isMountedRef.current && onCancelReply) {
          onCancelReply();
        }
      } catch (error) {
        console.error('Failed to send audio message:', error);
      }
    },
    [chatId, dispatch, onCancelReply]
  );

  const handleSend = useCallback(async () => {
    if (!message.trim() && !file) return;

    const currentMessage = message;
    const currentFile = file;

    setMessage('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    try {
      let response;
      if (currentFile) {
        const formData = new FormData();
        if (currentMessage) formData.append('content', currentMessage);
        formData.append('attachment', currentFile);
        if (replyingTo) {
          formData.append(
            'replyTo',
            JSON.stringify({
              id: replyingTo._id,
              senderName: replyingTo.senderId.name,
              content: replyingTo.content || 'Anexo',
            })
          );
        }
        response = await axios.post(`/api/internal-chat/${chatId}/messages`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        const payload = { content: currentMessage };
        if (replyingTo) {
          payload.replyTo = {
            id: replyingTo._id,
            senderName: replyingTo.senderId.name,
            content: replyingTo.content || 'Mensagem',
          };
        }
        response = await axios.post(`/api/internal-chat/${chatId}/messages`, payload);
      }

      if (response.data && isMountedRef.current) {
        dispatch(addMessage({ conversationId: chatId, message: response.data }));
      }
      if (isMountedRef.current && onCancelReply) {
        onCancelReply();
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  }, [message, file, replyingTo, chatId, dispatch, onCancelReply]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // Permissao de envio: owner, admin do grupo ou modo normal

    const isOwner = chatOwner && (typeof chatOwner === 'string' ? chatOwner === user._id : chatOwner === user._id || chatOwner?._id === user._id);
    const isParticipantAdmin = participants?.find(p => (p.userId?._id === user._id || p.userId === user._id))?.isAdmin;
    const canSend = !chatMode || chatMode === 'normal' || isOwner || isParticipantAdmin;

  if (!canSend) {
      return (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', bgcolor: 'background.neutral' }}>
              <Typography variant="body2" color="text.secondary">
                  Esta é uma conversa somente leitura.
              </Typography>
          </Box>
      );
  }

  return (
    <Box sx={{ p: 2, borderTop: '1px solid rgba(145, 158, 171, 0.24)' }}>
      {/* Reply Banner */}
      {replyingTo && (
        <Box
          sx={{
            p: 1,
            pl: 2,
            mb: 1,
            bgcolor: 'background.neutral',
            borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Respondendo a {replyingTo.senderId.name}
            </Typography>
            <Typography variant="body2" noWrap sx={{ color: 'text.secondary', maxWidth: 300 }}>
              {replyingTo.content || 'Anexo'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onCancelReply}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Box>
      )}

      {file && (
        <Box
          sx={{
            mb: 1,
            p: 1,
            border: '1px dashed grey',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {file.type.startsWith('image/') && (
              <Box
                component="img"
                src={file.preview}
                sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover', mr: 1 }}
              />
            )}
            {file.type.startsWith('video/') && (
              <Box
                component="video"
                src={file.preview}
                sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover', mr: 1 }}
              />
            )}
            <Typography variant="caption">{file.name}</Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              setFile(null);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          >
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {/* Announcement Mode Check */}
        {chatMode === 'announcement' && !participants?.find((p) => p.userId._id === user._id)?.isAdmin ? (
          <Box
            sx={{ width: '100%', textAlign: 'center', py: 1, bgcolor: 'background.neutral', borderRadius: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              Apenas administradores podem enviar mensagens neste grupo.
            </Typography>
          </Box>
        ) : (
          <>
            {isRecording ? (
              <RecorderPlayer onSend={handleAudioSend} onCancel={() => setIsRecording(false)} />
            ) : (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />

                <IconButton sx={{ mr: 1 }} onClick={handleOpenEmoji}>
                  <Iconify icon="eva:smiling-face-fill" width={24} height={24} />
                </IconButton>

                <Popover
                  open={openEmoji}
                  anchorEl={emojiAnchorEl}
                  onClose={handleCloseEmoji}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <Picker data={data} onEmojiSelect={handleEmojiSelect} theme="light" locale="pt" />
                </Popover>

                <IconButton sx={{ mr: 1 }} onClick={handleAttach}>
                  <Iconify icon="eva:attach-2-fill" width={24} height={24} />
                </IconButton>

                <IconButton sx={{ mr: 1 }} onClick={() => setIsRecording(true)} color="primary">
                  <Iconify icon="eva:mic-fill" width={24} height={24} />
                </IconButton>

                <TextField
                  fullWidth
                  size="small"
                  placeholder="Digite sua mensagem..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  onPaste={handlePaste}
                  multiline
                  maxRows={4}
                  inputProps={{ maxLength: 1024 }}
                />
                <Box component="span" sx={{ ml: 1 }}>
                  <IconButton
                    color="primary"
                    onClick={handleSend}
                    disabled={!message.trim() && !file}
                  >
                    <Iconify icon="eva:paper-plane-fill" width={24} height={24} />
                  </IconButton>
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
